import React, { useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { useTemplateStudioState } from 'state/hooks/template-studio.hook';

import Input from 'components/Input/Input';
import InputSelectDropdown, {
  InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';
import { ETemplateStudioLayoutOptions } from 'components/TemplatesStudio/enums';
import {
  IMultiInstanceFormProps,
  IOnePlusManyFormStoreBlock
} from 'components/TemplatesStudio/multiInstanceBlocks/multiInstanceBlocks.types';
import { Checkbox } from 'components/ui/checkbox';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { Separator } from 'components/ui/separator';

import { InlineFormRow } from '../../Components/InlineFormRow';
import { RewardsLayoutPicker } from '../../Components/RewardsLayoutPicker';
import { ThemesFormItem } from '../../Components/ThemesFormItem';

export const TemplateStudioBuyOneGetManyForm: React.FC<
  IMultiInstanceFormProps
> = ({ blockId }) => {
  const { control } = useFormContext();
  const { supportedThemesForRollingOffers } = useTemplateStudioState();

  const storeBlocks = useWatch({ control, name: 'storeBlocks' }) as
    | IOnePlusManyFormStoreBlock[]
    | undefined;
  const instanceIndex = useMemo(
    () =>
      (storeBlocks ?? []).findIndex(
        (block) => block.id === blockId || block.instanceKey === blockId
      ),
    [storeBlocks, blockId]
  );

  const desktopLayoutOptions = useMemo<InputSelectDropdownOption[]>(
    () =>
      Object.values(ETemplateStudioLayoutOptions).map((value) => ({
        value,
        label: value
      })),
    []
  );

  if (!supportedThemesForRollingOffers) return null;
  if (instanceIndex === -1) return null;

  const fieldPrefix = `storeBlocks.${instanceIndex}`;
  const themesFieldName = `${fieldPrefix}.themes`;
  const desktopLayoutFieldName = `${fieldPrefix}.desktopLayout`;
  const maxOffersValueFieldName = `${fieldPrefix}.maxOffers.value`;
  const maxOffersUnlimitedFieldName = `${fieldPrefix}.maxOffers.unlimited`;
  const rewardsLayoutFieldName = `${fieldPrefix}.rewardsLayout`;

  return (
    <React.Fragment>
      <Separator className="bg-base-border w-[288px] ml-[-2px]" />

      <FormField
        control={control}
        name={themesFieldName}
        render={({ field }) => (
          <ThemesFormItem
            label="Design Theme"
            onChange={field.onChange}
            value={field.value || []}
            supportedThemes={supportedThemesForRollingOffers}
            themesSelectDataTestId={`TemplatesStudio-BuyOneGetMany-${blockId}-themes`}
          />
        )}
      />

      <FormField
        control={control}
        name={desktopLayoutFieldName}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <InlineFormRow label="Desktop Layout">
                <InputSelectDropdown
                  options={desktopLayoutOptions}
                  defaultSelected={desktopLayoutOptions.find(
                    (layout) => layout.value === field.value
                  )}
                  onOptionChange={field.onChange}
                  placeholder="Select desktop layout"
                  isHorizontal={true}
                  buttonClassName={'h-[30px]'}
                  iconClassName="text-xs text-base-mutedForeground"
                  data-testid={`TemplatesStudio-BuyOneGetMany-${blockId}-desktopLayout-label`}
                  buttonDataTestId={`TemplatesStudio-BuyOneGetMany-${blockId}-desktopLayout-button`}
                  listDataTestId={`TemplatesStudio-BuyOneGetMany-${blockId}-desktopLayout-list`}
                />
              </InlineFormRow>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={maxOffersUnlimitedFieldName}
        render={({ field: unlimitedField }) => (
          <FormField
            control={control}
            name={maxOffersValueFieldName}
            render={({ field: valueField }) => {
              const isUnlimited = !!unlimitedField.value;
              return (
                <React.Fragment>
                  <FormItem>
                    <FormControl>
                      <InlineFormRow label="Max Offers">
                        <Input
                          name={maxOffersValueFieldName}
                          type="number"
                          min={1}
                          disabled={isUnlimited}
                          value={valueField.value}
                          inputClassName="w-full h-[30px]"
                          onChange={(event) =>
                            valueField.onChange(Number(event.target.value))
                          }
                          dataTestId={`TemplatesStudio-BuyOneGetMany-${blockId}-maxOffers-value`}
                        />
                      </InlineFormRow>
                    </FormControl>
                    <FormMessage />
                  </FormItem>

                  <FormItem>
                    <div className="flex items-center gap-2 mt-2">
                      <FormControl>
                        <Checkbox
                          id={`TemplatesStudio-BuyOneGetMany-${blockId}-maxOffers-unlimited`}
                          checked={unlimitedField.value}
                          onCheckedChange={(checked) =>
                            unlimitedField.onChange(checked === true)
                          }
                          data-testid={`TemplatesStudio-BuyOneGetMany-${blockId}-maxOffers-unlimited`}
                        />
                      </FormControl>
                      <label
                        htmlFor={`TemplatesStudio-BuyOneGetMany-${blockId}-maxOffers-unlimited`}
                        className="text-sm text-base-mutedForeground whitespace-nowrap cursor-pointer"
                      >
                        Unlimited
                      </label>
                    </div>
                    <FormMessage />
                  </FormItem>
                </React.Fragment>
              );
            }}
          />
        )}
      />

      <FormField
        control={control}
        name={rewardsLayoutFieldName}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <RewardsLayoutPicker
                value={field.value}
                onChange={field.onChange}
                testIdPrefix={`TemplatesStudio-BuyOneGetMany-${blockId}-rewardsLayout`}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </React.Fragment>
  );
};
